import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatPrice, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const FULFILMENT = ["unfulfilled", "processing", "shipped", "delivered", "cancelled"];
const PAYMENT = ["awaiting_payment", "paid", "cancelled", "refunded", "expired", "failed"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => { setLoading(true); try { const { data } = await api.get("/admin/orders"); setOrders(data); } catch {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const save = async (o) => {
    try {
      await api.put(`/admin/orders/${o.id}`, {
        fulfillment_status: o.fulfillment_status, tracking_number: o.tracking_number, internal_notes: o.internal_notes,
        payment_status: o.payment_status,
      });
      toast.success("Order updated"); setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl text-pitch mb-8">Orders</h1>
      <div className="bg-white border border-linen overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead><tr className="text-left text-stonemuted border-b border-linen">
            {["Order", "Customer", "Total", "Payment", "Fulfilment", "Date"].map((h) => <th key={h} className="px-4 py-3 font-mono text-xs uppercase tracking-wider">{h}</th>)}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="px-4 py-12 text-center text-stonemuted">Loading…</td></tr>
              : orders.length === 0 ? <tr><td colSpan={6} className="px-4 py-12 text-center text-stonemuted" data-testid="admin-no-orders">No orders yet.</td></tr>
              : orders.map((o) => (
                <tr key={o.id} onClick={() => setSelected({ ...o })} className="border-b border-linen/60 hover:bg-bone/50 cursor-pointer" data-testid="admin-order-row">
                  <td className="px-4 py-3 font-mono">{o.id}</td>
                  <td className="px-4 py-3">{o.customer?.name}<div className="text-xs text-stonemuted">{o.customer?.email}</div></td>
                  <td className="px-4 py-3">{formatPrice(o.total)}</td>
                  <td className="px-4 py-3 uppercase text-xs font-mono">{o.payment_status}</td>
                  <td className="px-4 py-3 uppercase text-xs font-mono">{o.fulfillment_status}</td>
                  <td className="px-4 py-3 text-xs text-stonemuted">{new Date(o.created_at).toLocaleDateString("en-AU")}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-ivory max-w-lg max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="font-serif text-2xl">Order {selected.id}</DialogTitle></DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="bg-bone p-4">
                  <div className="eyebrow mb-2">Customer</div>
                  <div>{selected.customer?.name} · {selected.customer?.email} · {selected.customer?.phone}</div>
                  <div className="mt-2 text-charcoal/70">{selected.shipping_address?.address}, {selected.shipping_address?.suburb} {selected.shipping_address?.state} {selected.shipping_address?.postcode}, {selected.shipping_address?.country}</div>
                  <div className="mt-2 text-xs font-mono uppercase tracking-wider text-charcoal/70" data-testid="admin-order-method">
                    Method: {selected.payment_method === "bank_transfer" ? "Bank Transfer" : (selected.payment_method || "—")}
                    {selected.bank_snapshot?.reference ? ` · Ref ${selected.bank_snapshot.reference}` : ""}
                  </div>
                </div>
                <div>
                  <div className="eyebrow mb-2">Items</div>
                  {selected.items?.map((i, idx) => <div key={idx} className="flex justify-between py-1"><span>{i.brand} {i.title}</span><span>{formatPrice(i.price)}</span></div>)}
                  <div className="flex justify-between py-1 text-stonemuted"><span>Shipping</span><span>{formatPrice(selected.shipping)}</span></div>
                  {selected.discount_amount > 0 && <div className="flex justify-between py-1 text-emeraldauth"><span>Discount ({selected.discount_code})</span><span>−{formatPrice(selected.discount_amount)}</span></div>}
                  <div className="flex justify-between py-2 border-t border-linen mt-1 font-medium"><span>Total</span><span>{formatPrice(selected.total)}</span></div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Payment Status</label>
                  <select value={selected.payment_status} onChange={(e) => setSelected({ ...selected, payment_status: e.target.value })}
                    data-testid="admin-order-payment-status" className="w-full border border-linen bg-white px-3 py-2.5 text-sm outline-none focus:border-pitch capitalize">
                    {PAYMENT.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </select>
                  <p className="text-[11px] text-stonemuted mt-1.5">Set to <strong>paid</strong> once a bank transfer clears — this marks the piece as sold. Setting <strong>cancelled/expired</strong> releases it back to inventory.</p>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Fulfilment Status</label>
                  <select value={selected.fulfillment_status} onChange={(e) => setSelected({ ...selected, fulfillment_status: e.target.value })}
                    data-testid="admin-order-status" className="w-full border border-linen bg-white px-3 py-2.5 text-sm outline-none focus:border-pitch capitalize">
                    {FULFILMENT.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Tracking Number</label>
                  <input value={selected.tracking_number || ""} onChange={(e) => setSelected({ ...selected, tracking_number: e.target.value })}
                    data-testid="admin-order-tracking" className="w-full border border-linen bg-white px-3 py-2.5 text-sm outline-none focus:border-pitch" />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Internal Notes <span className="text-vermilion normal-case">(never shown to customer)</span></label>
                  <textarea value={selected.internal_notes || ""} onChange={(e) => setSelected({ ...selected, internal_notes: e.target.value })} rows={3}
                    className="w-full border border-linen bg-white px-3 py-2.5 text-sm outline-none focus:border-pitch" />
                </div>
                <button onClick={() => save(selected)} data-testid="admin-order-save"
                  className="w-full bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] py-3.5 hover:bg-charcoal transition-colors">Save Order</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
